import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import CampaignsPage from './Campaigns/CampaignsPage';
import InfiniteConciergePage from './InfiniteConcierge/InfiniteConciergePage';
import LoyaltyCustomRulePage from './LoyaltyCustomRulePage';
import LoyaltyManualAccrualsPage from './LoyaltyManualAccrualsPage';
import StatsPage from './Stats/StatsPage';

const LOYALTY_ADMIN_ROLES = [ROLE_LUX_LOYALTY_ADMIN];

interface TabConfig {
  label: string;
  slug: string;
  component: React.ComponentType;
  requiredRoles?: Array<string>;
}

export const TabsMap: Record<string, TabConfig> = {
  campaigns: {
    label: 'Campaigns',
    slug: 'campaigns',
    component: CampaignsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  stats: {
    label: 'Offers',
    slug: 'stats',
    component: StatsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  infinite: {
    label: 'Infinite',
    slug: 'infinite',
    component: InfiniteConciergePage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'manual-accruals': {
    label: 'Manual Accruals',
    slug: 'manual-accruals',
    component: LoyaltyManualAccrualsPage,
  },
  'custom-rule': {
    label: 'Custom Rules',
    slug: 'custom-rule',
    component: LoyaltyCustomRulePage,
  },
} as const;

type TabKey = keyof typeof TabsMap;

preRegisterSpotlightRoutes(expandTabRoutes('/societe', 'Societe', TabsMap));

export default function LoyaltyPageContainer() {
  const { section: rawSection } = useParams<{ section?: TabKey }>();
  const { user } = useCurrentUser();

  const programName = LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName;

  const visibleTabs = Object.keys(TabsMap).filter((tab) => {
    const { requiredRoles } = TabsMap[tab];
    if (!requiredRoles) return true;
    return canShowTab(user, requiredRoles);
  });

  const currentTab = TabsMap[rawSection];
  const isTabVisible = visibleTabs.includes(rawSection);
  const CurrentComponent = isTabVisible ? currentTab?.component : undefined;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{programName}</title>
      </Helmet>

      <Box
        sx={(theme) => ({
          mt: 2,
          mb: 2,
          px: { xs: 2.5, md: 3 },
          py: 1.5,
          borderRadius: 2,
          color: 'common.white',
          background: `linear-gradient(120deg, ${theme.palette.loyaltyTiers.platinum.dark} 0%, ${theme.palette.loyaltyTiers.platinum.main} 55%, ${theme.palette.loyaltyTiers.infinite.dark} 100%)`,
          boxShadow: theme.shadows[1],
          '& .MuiTypography-root': { color: 'common.white' },
        })}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
          <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.8 }}>
            Loyalty
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
            {programName}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            · Tier rules, accruals, campaigns and the Infinite concierge programme
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={(theme) => ({
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1.5,
          '& .MuiTab-root': {
            minHeight: 48,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.secondary',
            transition: 'color 0.2s ease',
            '&:hover': { color: 'text.primary' },
          },
          '& .MuiTab-root.Mui-selected': {
            color: theme.palette.primary.main,
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        })}
      >
        <Tabs value={isTabVisible ? rawSection : false} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={TabsMap[tab].label}
              to={`/societe/${tab}`}
              component={Link}
              sx={{ minWidth: 30, px: 1.5 }}
            />
          ))}
        </Tabs>
      </Box>

      <Box>{CurrentComponent && <CurrentComponent />}</Box>
    </Container>
  );
}
